import React, {PropsWithChildren} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {HStack, Box, Text, useMultiStyleConfig, VStack, Link, Stack} from '@chakra-ui/react'
import {Icon, IconButton, Picture, QuantitySelector} from '_uiComponents'
import {BasketProductModel} from '_types'
import {useBasket, useViewport, useAnalytics} from '_customHooks'
import {updateCloudinarySize} from '_utils'

interface MinicartProductProps {
    product: BasketProductModel
}

/**
 * @name MinicartProduct
 *
 * @component
 *
 * @description
 * This component displays a product within a mini cart interface.
 *
 * @param {MinicartProductProps} props - The properties passed to the component.
 * @param {BasketProductModel} props.product - The product model containing details of the product to be displayed.
 *
 * @returns {JSX.Element} A JSX element representing the product in the mini cart.
 */
const MinicartProduct: React.FC<MinicartProductProps> = ({product}) => {
    const {isMobile} = useViewport()
    const {formatMessage} = useIntl()
    const {analyticsEventMinicartAddToCart, analyticsEventMinicartRemoveFromCart} = useAnalytics()
    const {updateItemInBasket, removeItemFromBasket} = useBasket()

    const isOrderable = product.c_isOrderable && product.c_price?.price?.value > 0
    const styles = useMultiStyleConfig('ProductTileCart', {disabled: !isOrderable}) as Record<string, PropsWithChildren>

    const triggerAnalyticsAddToCart = (product: BasketProductModel): void => {
        analyticsEventMinicartAddToCart({
            name: product.c_analytics.shortDescription,
            masterId: product.c_analytics.productId,
            price: product.c_price.price.value,
            categoryPath: product.c_analytics.categoryPath,
            color: product.c_analytics.color,
            size: product.c_analytics.size,
            gender: product.c_analytics.gender,
            hasDiscount: product.c_price.hasDiscount,
            currencyCode: product.c_price.currency,
        })
    }

    const triggerAnalyticsRemoveFromCart = (product: BasketProductModel, quantity?: number): void => {
        analyticsEventMinicartRemoveFromCart({
            name: product.c_analytics.shortDescription,
            masterId: product.c_analytics.productId,
            price: product.c_price.price.value,
            categoryPath: product.c_analytics.categoryPath,
            color: product.c_analytics.color,
            size: product.c_analytics.size,
            gender: product.c_analytics.gender,
            quantity: quantity,
            hasDiscount: product.c_price.hasDiscount,
            currencyCode: product.c_price.currency,
        })
    }

    const handleScreenReaderMessage = (shortDescription: string, quantity: number, action?: string) => {
        const messageAdd = formatMessage(
            {id: 'msg.sr.product.quantity.updated', defaultMessage: '{0} quantity updated to {1}'},
            {0: shortDescription, 1: quantity.toString()},
        )
        const messageRemove = formatMessage({id: 'msg.sr.product.removed', defaultMessage: 'Product {0} was removed from the cart'}, {0: shortDescription})
        const element = document.querySelector('span.sr-only')
        if (element) {
            if (action === 'quantitySelector') {
                element.textContent = messageAdd
            } else {
                element.textContent = messageRemove
            }
        }
    }

    const MinicartProductHeader = () => (
        <HStack {...styles.header}>
            <Link href={product.c_pdpUrl} {...styles.nameLink}>
                <Text as="span" {...styles.name} data-testid={`minicart-product-${product.productId}-name`}>
                    {product.c_shortDescription}
                </Text>
            </Link>

            <IconButton
                variant="iconButtonBgTransparent"
                size="iconButtonMd"
                icon={<Icon name="IconTrash" isFilled={false} size="md" />}
                aria-label={formatMessage({id: 'text.remove.line.item', defaultMessage: 'Remove product {0}'}, {0: product.c_shortDescription})}
                onClick={() => {
                    handleScreenReaderMessage(product.c_shortDescription, 0)
                    void removeItemFromBasket(product.itemId as string).then(() => triggerAnalyticsRemoveFromCart(product, product.quantity))
                }}
                data-testid={`minicart-product-${product.productId}-remove-button`}
            />
        </HStack>
    )

    return (
        <VStack {...styles.wrapper} data-testid={`minicart-product-${product.productId}`}>
            {isMobile && <MinicartProductHeader />}

            <HStack {...styles.body}>
                <Picture
                    {...styles.image}
                    imagexs={updateCloudinarySize(product.c_image?.url, 250)}
                    imagesm={updateCloudinarySize(product.c_image?.url, 250)}
                    imagemd={updateCloudinarySize(product.c_image?.url, 250)}
                    imagelg={updateCloudinarySize(product.c_image?.url, 250)}
                    imagexl={updateCloudinarySize(product.c_image?.url, 250)}
                    alt={product.c_image?.alt ?? undefined}
                />

                <VStack {...styles.informationsWrapper}>
                    {!isMobile && <MinicartProductHeader />}

                    <VStack {...styles.productInformations}>
                        <Text {...styles.sizeAndColor} data-testid={`minicart-product-${product.productId}-color`}>
                            <FormattedMessage id="product.label.color" defaultMessage="Color:" /> {product.c_color?.label}
                        </Text>

                        {isOrderable && (
                            <Text {...styles.sizeAndColor} data-testid={`minicart-product-${product.productId}-size`}>
                                <FormattedMessage id="product.label.size" defaultMessage="Size:" /> {product.c_size?.label}
                            </Text>
                        )}

                        {!isOrderable && (
                            <Text {...styles.disabledProductLabel} data-testid={`minicart-product-${product.productId}-not-available`}>
                                <FormattedMessage id="product.label.notavailable" defaultMessage="product no longer available" />
                            </Text>
                        )}

                        <Text {...styles.sku} data-testid={`minicart-product-${product.productId}-sku`}>
                            <FormattedMessage id="product.item" defaultMessage="SKU" /> {product.productId}
                        </Text>
                    </VStack>

                    <Stack {...styles.footer}>
                        {isOrderable && (
                            <Box flexGrow={1} data-testid={`minicart-product-${product.productId}-quantity-selector`}>
                                <QuantitySelector
                                    initialQuantity={product.quantity as number}
                                    minQuantity={1}
                                    maxQuantity={product.c_ATS}
                                    onChangeQuantity={(newQuantity, oldQuantity) => {
                                        void updateItemInBasket(product.itemId as string, newQuantity).then(() => {
                                            if (newQuantity > oldQuantity) {
                                                triggerAnalyticsAddToCart(product)
                                            } else {
                                                triggerAnalyticsRemoveFromCart(product)
                                            }
                                            handleScreenReaderMessage(product.c_shortDescription, newQuantity, 'quantitySelector')
                                        })
                                    }}
                                    ariaSubject={product.c_shortDescription}
                                />
                            </Box>
                        )}

                        <VStack {...styles.priceSection}>
                            {product.c_price?.hasDiscount && <Text {...styles.fullPrice}>{product.c_price.defaultPrice?.formattedValue}</Text>}

                            <Text data-testid={`minicart-product-${product.productId}-price`}>{product.c_price?.price?.formattedValue}</Text>
                        </VStack>
                    </Stack>
                </VStack>
            </HStack>
        </VStack>
    )
}

export {MinicartProduct}
